import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

// Registrar todos los componentes de Chart.js
Chart.register(...registerables)

/**
 * ChartController
 * 
 * Controlador Stimulus para renderizar gráficas con Chart.js
 * 
 * Uso:
 *   <canvas 
 *     data-controller="chart"
 *     data-chart-type-value="line"
 *     data-chart-data-value='{"labels": [...], "sales": [...], "orders": [...]}'
 *   ></canvas>
 */
export default class extends Controller {
  static values = {
    type: { type: String, default: "line" },
    data: Object
  }

  connect() {
    this.initChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  initChart() {
    const ctx = this.element.getContext("2d")
    const config = this.buildConfig()
    
    this.chart = new Chart(ctx, config)
  }

  buildConfig() {
    const type = this.typeValue
    const data = this.dataValue

    // Configuración base compartida
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            usePointStyle: true,
            padding: 20,
            color: this.getTextColor()
          }
        }
      }
    }

    switch (type) {
      case "line":
        return this.buildLineChart(data, baseOptions)
      case "bar":
        return this.buildBarChart(data, baseOptions)
      case "doughnut":
        return this.buildDoughnutChart(data, baseOptions)
      case "pie":
        return this.buildPieChart(data, baseOptions)
      default:
        return this.buildLineChart(data, baseOptions)
    }
  }

  buildLineChart(data, baseOptions) {
    return {
      type: "line",
      data: {
        labels: data.labels || [],
        datasets: [
          {
            label: "Ventas ($)",
            data: data.sales || [],
            borderColor: "rgb(59, 130, 246)",
            backgroundColor: "rgba(59, 130, 246, 0.1)",
            fill: true,
            tension: 0.4,
            yAxisID: "y"
          },
          {
            label: "Órdenes",
            data: data.orders || [],
            borderColor: "rgb(16, 185, 129)",
            backgroundColor: "rgba(16, 185, 129, 0.1)",
            fill: false,
            tension: 0.4,
            yAxisID: "y1"
          }
        ]
      },
      options: {
        ...baseOptions,
        interaction: {
          mode: "index",
          intersect: false
        },
        scales: {
          x: {
            grid: {
              color: this.getGridColor()
            },
            ticks: {
              color: this.getTextColor()
            }
          },
          y: {
            type: "linear",
            display: true,
            position: "left",
            title: {
              display: true,
              text: "Ventas ($)",
              color: this.getTextColor()
            },
            grid: {
              color: this.getGridColor()
            },
            ticks: {
              color: this.getTextColor(),
              callback: (value) => "$" + value.toLocaleString()
            }
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            title: {
              display: true,
              text: "Órdenes",
              color: this.getTextColor()
            },
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              color: this.getTextColor()
            }
          }
        }
      }
    }
  }

  buildBarChart(data, baseOptions) {
    return {
      type: "bar",
      data: {
        labels: data.labels || [],
        datasets: [{
          label: data.label || "Valores",
          data: data.values || [],
          backgroundColor: this.getChartColors(data.values?.length || 0),
          borderRadius: 4
        }]
      },
      options: {
        ...baseOptions,
        scales: {
          x: {
            grid: { color: this.getGridColor() },
            ticks: { color: this.getTextColor() }
          },
          y: {
            grid: { color: this.getGridColor() },
            ticks: { color: this.getTextColor() }
          }
        }
      }
    }
  }

  buildDoughnutChart(data, baseOptions) {
    return {
      type: "doughnut",
      data: {
        labels: data.labels || [],
        datasets: [{
          data: data.values || [],
          backgroundColor: this.getChartColors(data.values?.length || 0),
          borderWidth: 2,
          borderColor: this.getBorderColor()
        }]
      },
      options: {
        ...baseOptions,
        cutout: "60%",
        plugins: {
          ...baseOptions.plugins,
          legend: {
            ...baseOptions.plugins.legend,
            position: "right"
          }
        }
      }
    }
  }

  buildPieChart(data, baseOptions) {
    return {
      type: "pie",
      data: {
        labels: data.labels || [],
        datasets: [{
          data: data.values || [],
          backgroundColor: this.getChartColors(data.values?.length || 0),
          borderWidth: 2,
          borderColor: this.getBorderColor()
        }]
      },
      options: baseOptions
    }
  }

  // Paleta de colores para gráficas
  getChartColors(count) {
    const colors = [
      "rgba(59, 130, 246, 0.8)",   // Blue
      "rgba(16, 185, 129, 0.8)",   // Green
      "rgba(245, 158, 11, 0.8)",   // Amber
      "rgba(239, 68, 68, 0.8)",    // Red
      "rgba(139, 92, 246, 0.8)",   // Purple
      "rgba(236, 72, 153, 0.8)",   // Pink
      "rgba(20, 184, 166, 0.8)",   // Teal
      "rgba(249, 115, 22, 0.8)",   // Orange
      "rgba(99, 102, 241, 0.8)",   // Indigo
      "rgba(34, 197, 94, 0.8)"     // Emerald
    ]
    return colors.slice(0, count)
  }

  // Detectar tema oscuro
  isDarkMode() {
    return document.documentElement.getAttribute("data-theme") === "dark" ||
           window.matchMedia("(prefers-color-scheme: dark)").matches
  }

  getTextColor() {
    return this.isDarkMode() ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)"
  }

  getGridColor() {
    return this.isDarkMode() ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
  }

  getBorderColor() {
    return this.isDarkMode() ? "rgba(30, 30, 30, 1)" : "rgba(255, 255, 255, 1)"
  }
}
